import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useTranslation } from 'react-i18next';

/**
 * Loading state for the EFD agent's opening snapshot.
 *
 * Shows the real backend progress (the /welcome SSE stream emits `stage`
 * events) as a three-step list, plus a shimmering skeleton of the layout that
 * is about to arrive — a short paragraph, a small table and two bullets — so
 * the wait reads as "your overview is being built", not "something hangs".
 *
 * Purely presentational; the parent owns the stage state.
 *   stage: 'connecting' | 'querying' | 'composing'
 */

const STEPS = ['connecting', 'querying', 'composing'];

// Sweep of light travelling across every skeleton element.
const shimmerSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '6px',
  bgcolor: 'rgba(255, 255, 255, 0.05)',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    transform: 'translateX(-100%)',
    background:
      'linear-gradient(90deg, transparent 0%, rgba(25, 208, 239, 0.14) 45%, rgba(25, 208, 239, 0.22) 55%, transparent 100%)',
    animation: 'efdShimmer 1.6s ease-in-out infinite',
  },
};

function SkeletonBar({ width, height = 11, delay = 0, sx }) {
  return (
    <Box
      sx={{
        ...shimmerSx,
        width,
        height,
        ...sx,
        '&::after': { ...shimmerSx['&::after'], animationDelay: `${delay}ms` },
      }}
    />
  );
}

function EfdWelcomeLoader({ stage = 'connecting' }) {
  const { t } = useTranslation();
  const activeIndex = Math.max(0, STEPS.indexOf(stage));

  return (
    <Box
      sx={{
        // Keyframes are declared once here and used by every child.
        '@keyframes efdShimmer': { '100%': { transform: 'translateX(100%)' } },
        '@keyframes efdPulse': {
          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
          '50%': { opacity: 0.45, transform: 'scale(0.82)' },
        },
        '@keyframes efdRing': {
          '0%': { transform: 'scale(0.9)', opacity: 0.55 },
          '70%, 100%': { transform: 'scale(2.1)', opacity: 0 },
        },
        '@keyframes efdRise': {
          from: { opacity: 0, transform: 'translateY(6px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        width: '100%',
        borderRadius: '10px',
        border: '1px solid rgba(25, 208, 239, 0.22)',
        bgcolor: 'rgba(25, 208, 239, 0.04)',
        p: 1.75,
        animation: 'efdRise 320ms ease-out both',
      }}
    >
      {/* ── Progress steps ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
        {STEPS.map((step, i) => {
          const done = i < activeIndex;
          const active = i === activeIndex;
          return (
            <Box
              key={step}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                opacity: done ? 0.65 : active ? 1 : 0.3,
                transition: 'opacity 320ms ease',
              }}
            >
              {/* Marker: check when done, pulsing dot with halo while active. */}
              <Box
                sx={{
                  position: 'relative',
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {done ? (
                  <CheckRoundedIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                ) : (
                  <>
                    {active && (
                      <Box
                        sx={{
                          position: 'absolute',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          border: '1.5px solid',
                          borderColor: 'primary.main',
                          animation: 'efdRing 1.5s ease-out infinite',
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        width: active ? 8 : 6,
                        height: active ? 8 : 6,
                        borderRadius: '50%',
                        bgcolor: active ? 'primary.main' : 'text.secondary',
                        animation: active ? 'efdPulse 1.5s ease-in-out infinite' : 'none',
                      }}
                    />
                  </>
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: active ? 'primary.main' : 'text.secondary',
                  fontWeight: active ? 600 : 400,
                  transition: 'color 320ms ease',
                }}
              >
                {t(`efd_agent_stage_${step}`)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ── Skeleton of the message that is coming ── */}
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* intro line */}
        <SkeletonBar width="72%" delay={0} />

        {/* table: header row + three rows of three cells */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr 1fr',
            gap: '6px',
            border: '1px solid rgba(255, 255, 255, 0.07)',
            borderRadius: '8px',
            p: 1,
          }}
        >
          {[0, 1, 2].map((c) => (
            <SkeletonBar key={`h${c}`} width="100%" height={9} delay={80 * c} sx={{ bgcolor: 'rgba(25, 208, 239, 0.10)' }} />
          ))}
          {[0, 1, 2].map((r) =>
            [0, 1, 2].map((c) => (
              <SkeletonBar key={`${r}-${c}`} width={c === 0 ? '90%' : '70%'} height={9} delay={120 * r + 60 * c} />
            ))
          )}
        </Box>

        {/* two bullet lines */}
        {[0, 1].map((i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'rgba(25, 208, 239, 0.4)', flexShrink: 0 }} />
            <SkeletonBar width={i === 0 ? '84%' : '66%'} height={9} delay={200 + 120 * i} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default EfdWelcomeLoader;
